// Stream-token re-mint helpers (shared by the live-view players).
//
// The signed HLS URL embeds a short-lived (15-min) stream JWT as ``?token=``.
// hls.js loads that URL once; when the token expires MediaMTX's auth callback
// 401s every subsequent playlist/segment fetch and the tile dies. These
// helpers let a player (a) proactively re-mint just BEFORE expiry and (b)
// reactively re-mint on a 401/403, then re-init the source — no page reload.
//
// The two URL/JWT helpers are pure so they can be unit-tested without a media
// element or a live token; ``fetchFreshStreamUrl`` is the one network call.
import { api } from "@/api/client";

export interface StreamTokenResponseT {
  token: string;
  stream_url: string;
  expires_at?: unknown;
}

/** GET a freshly-minted signed stream URL for a camera. The ``api`` client
 *  carries auth (httpOnly cookie) and the super-admin ``?tenant_id=`` scope
 *  automatically, so this is correct for switched-tenant viewers too. */
export async function fetchFreshStreamUrl(cameraId: string): Promise<string> {
  const { data } = await api.get<StreamTokenResponseT>(
    `/api/v1/streaming/tokens/${cameraId}`,
  );
  return data.stream_url;
}

function b64UrlDecode(segment: string): string {
  const padLen = segment.length % 4 === 0 ? 0 : 4 - (segment.length % 4);
  const b64 = segment.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat(padLen);
  return atob(b64);
}

/** Parse the stream JWT's ``exp`` (epoch **ms**) from a signed HLS URL's
 *  ``?token=`` param. Returns null when there is no token, it isn't a JWT, or
 *  it carries no numeric ``exp`` — callers treat null as "expiry unknown". */
export function streamTokenExpiryMs(url: string | null | undefined): number | null {
  if (!url) return null;
  try {
    const queryStart = url.indexOf("?");
    if (queryStart === -1) return null;
    const token = new URLSearchParams(url.slice(queryStart + 1)).get("token");
    if (!token) return null;
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const payload = JSON.parse(b64UrlDecode(parts[1])) as { exp?: unknown };
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/** Milliseconds from ``nowMs`` until we should proactively re-mint: ``skewMs``
 *  before the token's expiry, floored at 0. Returns null when the expiry can't
 *  be determined (caller then relies on the reactive 401 path only). */
export function msUntilRemint(
  url: string | null | undefined,
  nowMs: number,
  skewMs = 60_000,
): number | null {
  const exp = streamTokenExpiryMs(url);
  if (exp === null) return null;
  return Math.max(0, exp - skewMs - nowMs);
}

/** True for the HTTP statuses that mean "stream token rejected" — the signal to
 *  re-mint rather than retry the same (stale-token) URL. */
export function isStreamAuthError(status: number | undefined | null): boolean {
  return status === 401 || status === 403;
}
